import React, { useState, useEffect } from 'react';

interface WelcomePianoProps {
  adminName: string;
}

type Instrument = 'piano' | 'guitar' | 'bass' | 'synth' | 'drums';

interface RecordingNote {
  note: string;
  instrument: Instrument;
  timestamp: number;
}

interface Track {
  id: string;
  name: string;
  instrument: Instrument;
  notes: RecordingNote[];
  color: string;
  startOffset: number; // When the track should start playing (in ms)
  duration: number; // Total duration of the track (in ms)
  volume: number; // Track volume (0-1)
}

type PlaybackMode = 'simultaneous' | 'sequential';

const WelcomePiano: React.FC<WelcomePianoProps> = ({ adminName }) => {
  const [playingNote, setPlayingNote] = useState<string | null>(null);
  const [recording, setRecording] = useState<string[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [waveform, setWaveform] = useState<OscillatorType>('sine');
  const [volume, setVolume] = useState(0.3);
  const [reverb, setReverb] = useState(false);
  const [isLooping, setIsLooping] = useState(false);
  const [loopInterval, setLoopInterval] = useState<NodeJS.Timeout | null>(null);
  const [tempo, setTempo] = useState(400); // ms between notes

  const [instrument, setInstrument] = useState<Instrument>('piano');
  const [showControls, setShowControls] = useState(false);

  // Track Studio
  const [tracks, setTracks] = useState<Track[]>([]);
  const [currentTrackRecording, setCurrentTrackRecording] = useState<RecordingNote[]>([]);
  const [recordingStartTime, setRecordingStartTime] = useState<number | null>(null);
  const [showTrackStudio, setShowTrackStudio] = useState(false);
  const [playbackMode] = useState<PlaybackMode>('simultaneous');
  const [recordingTrackId, setRecordingTrackId] = useState<string | null>(null);
  const [draggingTrackId, setDraggingTrackId] = useState<string | null>(null);
  const [timelineZoom, setTimelineZoom] = useState(1); // 1 = 100%, 2 = 200%, etc.
  const [playbackProgress, setPlaybackProgress] = useState(0); // Current playback position in ms
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPresetMenu, setShowPresetMenu] = useState(false);
  const [playbackTimeouts, setPlaybackTimeouts] = useState<NodeJS.Timeout[]>([]);
  const [progressInterval, setProgressInterval] = useState<NodeJS.Timeout | null>(null);

  // Piyano notaları ve frekansları
  const pianoKeys = [
    { note: 'C', freq: 261.63, color: 'white', label: 'Do' },
    { note: 'C#', freq: 277.18, color: 'black', label: 'Do#' },
    { note: 'D', freq: 293.66, color: 'white', label: 'Re' },
    { note: 'D#', freq: 311.13, color: 'black', label: 'Re#' },
    { note: 'E', freq: 329.63, color: 'white', label: 'Mi' },
    { note: 'F', freq: 349.23, color: 'white', label: 'Fa' },
    { note: 'F#', freq: 369.99, color: 'black', label: 'Fa#' },
    { note: 'G', freq: 392.00, color: 'white', label: 'Sol' },
    { note: 'G#', freq: 415.30, color: 'black', label: 'Sol#' },
    { note: 'A', freq: 440.00, color: 'white', label: 'La' },
    { note: 'A#', freq: 466.16, color: 'black', label: 'La#' },
    { note: 'B', freq: 493.88, color: 'white', label: 'Si' },
  ];

  // Instrument sound generators with improved realism
  const getInstrumentSound = (frequency: number, instrument: Instrument, audioContext: AudioContext) => {
    switch (instrument) {
      case 'guitar':
        return {
          type: 'sawtooth' as OscillatorType,
          attackTime: 0.01,
          decayTime: 1.5,
          volume: volume * 0.8,
          useHarmonics: true // Guitar harmonics
        };
      case 'bass':
        return {
          type: 'triangle' as OscillatorType, // Triangle wave for warmer bass
          attackTime: 0.03,
          decayTime: 1.2,
          volume: volume * 1.4,
          frequencyMultiplier: 0.25, // Two octaves lower for deep bass
          useSubBass: true // Add sub-bass layer
        };
      case 'synth':
        return {
          type: 'square' as OscillatorType,
          attackTime: 0.02,
          decayTime: 0.8,
          volume: volume * 0.7
        };
      case 'drums':
        return {
          type: 'sine' as OscillatorType,
          attackTime: 0.001,
          decayTime: 0.15,
          volume: volume * 2.0, // Louder drums
          frequencyMultiplier: 0.2,
          useNoise: true // Add white noise for realistic drum sound
        };
      default: // piano
        return {
          type: waveform,
          attackTime: 0.02,
          decayTime: 0.5,
          volume: volume
        };
    }
  };

  const playSound = (frequency: number, note: string, duration: number = 0.5, customInstrument?: Instrument, trackVolume: number = 1.0) => {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    const instrumentToUse = customInstrument || instrument;
    const instrumentConfig = getInstrumentSound(frequency, instrumentToUse, audioContext);
    const finalFrequency = frequency * (instrumentConfig.frequencyMultiplier || 1);

    // Advanced bass guitar with sub-bass layer
    if (instrumentConfig.useSubBass) {
      const subOsc = audioContext.createOscillator();
      const subGain = audioContext.createGain();
      const filter = audioContext.createBiquadFilter();

      filter.type = 'lowpass';
      filter.frequency.value = 200; // Low-pass filter for bass
      filter.Q.value = 1;

      subOsc.type = 'sine';
      subOsc.frequency.value = finalFrequency * 0.5; // One octave lower
      subGain.gain.setValueAtTime(instrumentConfig.volume * 0.6 * trackVolume, audioContext.currentTime);
      subGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + instrumentConfig.decayTime);

      subOsc.connect(subGain);
      subGain.connect(filter);
      filter.connect(audioContext.destination);

      subOsc.start(audioContext.currentTime);
      subOsc.stop(audioContext.currentTime + instrumentConfig.decayTime);
    }

    // Realistic drum sounds with noise
    if (instrumentConfig.useNoise && instrument === 'drums') {
      const noiseBuffer = audioContext.createBuffer(1, audioContext.sampleRate * 0.2, audioContext.sampleRate);
      const noiseData = noiseBuffer.getChannelData(0);
      for (let i = 0; i < noiseData.length; i++) {
        noiseData[i] = (Math.random() * 2 - 1) * 0.3; // White noise
      }

      const noiseSource = audioContext.createBufferSource();
      const noiseGain = audioContext.createGain();
      const noiseFilter = audioContext.createBiquadFilter();

      noiseFilter.type = 'bandpass';
      noiseFilter.frequency.value = finalFrequency * 8; // Higher freq for snare-like sound
      noiseFilter.Q.value = 2;

      noiseSource.buffer = noiseBuffer;
      noiseGain.gain.setValueAtTime(instrumentConfig.volume * 0.5 * trackVolume, audioContext.currentTime);
      noiseGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);

      noiseSource.connect(noiseFilter);
      noiseFilter.connect(noiseGain);
      noiseGain.connect(audioContext.destination);

      noiseSource.start(audioContext.currentTime);
    }

    // Guitar harmonics
    if (instrumentConfig.useHarmonics) {
      const harmonic = audioContext.createOscillator();
      const harmonicGain = audioContext.createGain();

      harmonic.type = 'sine';
      harmonic.frequency.value = finalFrequency * 2; // Second harmonic
      harmonicGain.gain.setValueAtTime(instrumentConfig.volume * 0.3 * trackVolume, audioContext.currentTime);
      harmonicGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + instrumentConfig.decayTime);

      harmonic.connect(harmonicGain);
      harmonicGain.connect(audioContext.destination);

      harmonic.start(audioContext.currentTime);
      harmonic.stop(audioContext.currentTime + instrumentConfig.decayTime);
    }

    // Reverb/Echo effect
    if (reverb) {
      const delay = audioContext.createDelay();
      const feedback = audioContext.createGain();

      delay.delayTime.value = 0.3;
      feedback.gain.value = 0.4;

      oscillator.connect(gainNode);
      gainNode.connect(delay);
      delay.connect(feedback);
      feedback.connect(delay);
      delay.connect(audioContext.destination);
      gainNode.connect(audioContext.destination);
    } else {
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
    }

    oscillator.frequency.value = finalFrequency;
    oscillator.type = instrumentConfig.type;

    gainNode.gain.setValueAtTime(instrumentConfig.volume * trackVolume, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + instrumentConfig.decayTime);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + instrumentConfig.decayTime);

    setPlayingNote(note);
    setTimeout(() => setPlayingNote(null), 200);

    // Track recording
    if (isRecording && recordingTrackId) {
      const currentTime = Date.now();
      if (!recordingStartTime) {
        setRecordingStartTime(currentTime);
      }
      setCurrentTrackRecording(prev => [...prev, {
        note,
        instrument,
        timestamp: currentTime - (recordingStartTime || currentTime)
      }]);
    } else if (isRecording) {
      setRecording(prev => [...prev, note]);
    }
  };

  const playRecording = () => {
    if (recording.length === 0) return;

    recording.forEach((note, index) => {
      setTimeout(() => {
        const key = pianoKeys.find(k => k.note === note);
        if (key) {
          playSound(key.freq, key.note);
        }
      }, index * tempo);
    });
  };

  const toggleLoop = () => {
    if (isLooping) {
      // Stop loop
      if (loopInterval) {
        clearInterval(loopInterval);
        setLoopInterval(null);
      }
      setIsLooping(false);
    } else {
      // Start loop
      if (recording.length === 0) return;

      setIsLooping(true);
      const totalDuration = recording.length * tempo;

      // Play immediately
      playRecording();

      // Then loop
      const interval = setInterval(() => {
        playRecording();
      }, totalDuration);

      setLoopInterval(interval);
    }
  };

  const clearRecording = () => {
    setRecording([]);
    setCurrentTrackRecording([]);
    setRecordingStartTime(null);
    setIsRecording(false);
    setRecordingTrackId(null);
    if (isLooping) {
      toggleLoop();
    }
  };

  // Track Studio Functions
  const getInstrumentColor = (inst: Instrument): string => {
    const colors = {
      piano: '#3b82f6',
      guitar: '#f59e0b',
      bass: '#a855f7',
      synth: '#06b6d4',
      drums: '#ef4444'
    };
    return colors[inst];
  };

  const getInstrumentIcon = (inst: Instrument): string => {
    const icons = { piano: '🎹', guitar: '🎸', bass: '🎵', synth: '🎛️', drums: '🥁' };
    return icons[inst];
  };

  const createNewTrack = () => {
    const trackNumber = tracks.length + 1;
    const newTrack: Track = {
      id: Date.now().toString(),
      name: `Track ${trackNumber} - ${instrument}`,
      instrument: instrument,
      notes: [],
      color: getInstrumentColor(instrument),
      startOffset: 0, // Start at beginning by default
      duration: 0, // Will be calculated after recording
      volume: 1.0 // Default volume at 100%
    };
    setTracks(prev => [...prev, newTrack]);
    return newTrack.id;
  };

  const startTrackRecording = () => {
    const trackId = createNewTrack();
    setRecordingTrackId(trackId);
    setCurrentTrackRecording([]);
    setRecordingStartTime(Date.now());
    setIsRecording(true);
  };

  const stopTrackRecording = () => {
    if (recordingTrackId && currentTrackRecording.length > 0) {
      // Calculate track duration from the last note's timestamp
      const duration = currentTrackRecording.length > 0
        ? Math.max(...currentTrackRecording.map(n => n.timestamp)) + 1000 // Add 1 second buffer
        : 0;

      setTracks(prev => prev.map(track =>
        track.id === recordingTrackId
          ? { ...track, notes: currentTrackRecording, duration }
          : track
      ));
    } else if (recordingTrackId) {
      // Remove empty track
      setTracks(prev => prev.filter(t => t.id !== recordingTrackId));
    }
    setIsRecording(false);
    setRecordingTrackId(null);
    setCurrentTrackRecording([]);
    setRecordingStartTime(null);
  };

  const deleteTrack = (trackId: string) => {
    setTracks(prev => prev.filter(t => t.id !== trackId));
  };

  const stopPlayback = () => {
    // Clear all scheduled timeouts
    playbackTimeouts.forEach(timeout => clearTimeout(timeout));
    setPlaybackTimeouts([]);

    // Clear progress interval
    if (progressInterval) {
      clearInterval(progressInterval);
      setProgressInterval(null);
    }

    setIsPlaying(false);
    setPlaybackProgress(0);
  };

  const playAllTracks = () => {
    if (tracks.length === 0) return;

    // Clear any existing playback
    stopPlayback();

    setIsPlaying(true);
    setPlaybackProgress(0);

    const totalDuration = getTimelineDuration();
    const startTime = Date.now();
    const timeouts: NodeJS.Timeout[] = [];

    // Update progress bar
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      if (elapsed >= totalDuration) {
        setPlaybackProgress(0);
        setIsPlaying(false);
        clearInterval(interval);
        setProgressInterval(null);
      } else {
        setPlaybackProgress(elapsed);
      }
    }, 16); // ~60fps

    setProgressInterval(interval);

    if (playbackMode === 'simultaneous') {
      // Play all tracks with their individual start offsets and instruments
      tracks.forEach(track => {
        track.notes.forEach((recordedNote) => {
          const timeout = setTimeout(() => {
            const key = pianoKeys.find(k => k.note === recordedNote.note);
            if (key) {
              playSound(key.freq, key.note, 0.5, track.instrument, track.volume);
            }
          }, track.startOffset + recordedNote.timestamp);
          timeouts.push(timeout);
        });
      });

      // Stop playback indicator after total duration
      const finalTimeout = setTimeout(() => {
        setIsPlaying(false);
        setPlaybackProgress(0);
        setPlaybackTimeouts([]);
      }, totalDuration);
      timeouts.push(finalTimeout);
    } else {
      // Play tracks sequentially (one after another)
      let cumulativeDelay = 0;
      tracks.forEach(track => {
        track.notes.forEach((recordedNote) => {
          const timeout = setTimeout(() => {
            const key = pianoKeys.find(k => k.note === recordedNote.note);
            if (key) {
              playSound(key.freq, key.note, 0.5, track.instrument, track.volume);
            }
          }, cumulativeDelay + recordedNote.timestamp);
          timeouts.push(timeout);
        });

        cumulativeDelay += track.duration;
      });

      // Stop playback indicator after total duration
      const finalTimeout = setTimeout(() => {
        setIsPlaying(false);
        setPlaybackProgress(0);
        setPlaybackTimeouts([]);
      }, cumulativeDelay);
      timeouts.push(finalTimeout);
    }

    setPlaybackTimeouts(timeouts);
  };

  const clearAllTracks = () => {
    setTracks([]);
    setCurrentTrackRecording([]);
    setRecordingStartTime(null);
    setRecordingTrackId(null);
  };

  // Timeline functions
  const updateTrackOffset = (trackId: string, newOffset: number) => {
    setTracks(prev => prev.map(track =>
      track.id === trackId
        ? { ...track, startOffset: Math.max(0, newOffset) }
        : track
    ));
  };

  const updateTrackVolume = (trackId: string, newVolume: number) => {
    setTracks(prev => prev.map(track =>
      track.id === trackId
        ? { ...track, volume: Math.max(0, Math.min(1, newVolume)) } // Clamp between 0 and 1
        : track
    ));
  };

  const handleTimelineMouseDown = (trackId: string, event: React.MouseEvent) => {
    setDraggingTrackId(trackId);
    event.preventDefault();
  };

  const handleTimelineMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!draggingTrackId) return;

    const timeline = event.currentTarget;
    const rect = timeline.getBoundingClientRect();
    const x = event.clientX - rect.left - 128; // Subtract label width (128px = w-32)

    // Convert pixel position to milliseconds (1 pixel = 10ms at zoom level 1)
    const pixelToMs = 10 / timelineZoom;
    const newOffset = Math.max(0, x * pixelToMs);

    updateTrackOffset(draggingTrackId, newOffset);
  };

  const handleTimelineMouseUp = () => {
    setDraggingTrackId(null);
  };

  // Calculate total timeline duration
  const getTimelineDuration = () => {
    if (tracks.length === 0) return 10000; // 10 seconds minimum
    return Math.max(...tracks.map(t => t.startOffset + t.duration), 10000);
  };

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const milliseconds = Math.floor((ms % 1000) / 100);
    return `${seconds}.${milliseconds}s`;
  };

  // Preset melodies with more variety
  const presets = {
    'happy_birthday': {
      name: 'Doğum Günü',
      notes: ['C', 'C', 'D', 'C', 'F', 'E', 'C', 'C', 'D', 'C', 'G', 'F'],
      instrument: 'piano' as Instrument
    },
    'jingle_bells': {
      name: 'Jingle Bells',
      notes: ['E', 'E', 'E', 'E', 'E', 'E', 'E', 'G', 'C', 'D', 'E'],
      instrument: 'piano' as Instrument
    },
    'twinkle': {
      name: 'Twinkle Star',
      notes: ['C', 'C', 'G', 'G', 'A', 'A', 'G', 'F', 'F', 'E', 'E', 'D', 'D', 'C'],
      instrument: 'piano' as Instrument
    },
    'fur_elise': {
      name: 'Für Elise',
      notes: ['E', 'D#', 'E', 'D#', 'E', 'B', 'D', 'C', 'A', 'C', 'E', 'A', 'B'],
      instrument: 'piano' as Instrument
    },
    'imperial_march': {
      name: 'Imperial March',
      notes: ['G', 'G', 'G', 'D#', 'A#', 'G', 'D#', 'A#', 'G'],
      instrument: 'synth' as Instrument
    },
    'smoke_on_water': {
      name: 'Smoke on Water',
      notes: ['G', 'A#', 'C', 'G', 'A#', 'C#', 'C', 'G', 'A#', 'C', 'A#', 'G'],
      instrument: 'guitar' as Instrument
    },
    'seven_nation': {
      name: 'Seven Nation Army',
      notes: ['E', 'E', 'G', 'E', 'D', 'C', 'B', 'E', 'E', 'G', 'E', 'D', 'C', 'D', 'C', 'B'],
      instrument: 'bass' as Instrument
    },
    'we_will_rock': {
      name: 'We Will Rock You',
      notes: ['C', 'C', 'D', 'C', 'C', 'D', 'C', 'C', 'D'],
      instrument: 'drums' as Instrument
    },
    'pink_panther': {
      name: 'Pink Panther',
      notes: ['C#', 'D', 'E', 'F', 'C#', 'D', 'E', 'F', 'C#', 'D', 'E', 'F', 'D#', 'E'],
      instrument: 'synth' as Instrument
    },
    'mario_theme': {
      name: 'Super Mario',
      notes: ['E', 'E', 'E', 'C', 'E', 'G', 'G', 'C', 'G', 'E'],
      instrument: 'synth' as Instrument
    }
  };

  const playPreset = (presetName: keyof typeof presets) => {
    const preset = presets[presetName];
    setRecording(preset.notes);
    setTimeout(() => {
      preset.notes.forEach((note, index) => {
        setTimeout(() => {
          const key = pianoKeys.find(k => k.note === note);
          if (key) {
            playSound(key.freq, key.note);
          }
        }, index * tempo);
      });
    }, 100);
  };

  const addPresetToTimeline = (presetName: keyof typeof presets) => {
    const preset = presets[presetName];
    // Convert preset notes to RecordingNote format
    const recordingNotes: RecordingNote[] = preset.notes.map((note, index) => ({
      note,
      instrument: preset.instrument,
      timestamp: index * tempo
    }));

    const duration = preset.notes.length * tempo + 1000;

    const newTrack: Track = {
      id: Date.now().toString(),
      name: `${preset.name}`,
      instrument: preset.instrument,
      notes: recordingNotes,
      color: getInstrumentColor(preset.instrument),
      startOffset: 0,
      duration,
      volume: 1.0 // Default volume at 100%
    };

    setTracks(prev => [...prev, newTrack]);
  };

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (loopInterval) {
        clearInterval(loopInterval);
      }
    };
  }, [loopInterval]);

  const [currentMessage, setCurrentMessage] = useState(() => {
    const welcomeMessages = [
      "Müzik dünyasına hoş geldin! 🎵",
      "Bugün harika işler yapacaksın! 🌟",
      "Sanatçıların seni bekliyor! 🎸",
      "Yeni bir gün, yeni melodiler! 🎹",
      "Ritmi sen belirle! 🥁"
    ];
    return welcomeMessages[Math.floor(Math.random() * welcomeMessages.length)];
  });

  useEffect(() => {
    const welcomeMessages = [
      "Müzik dünyasına hoş geldin! 🎵",
      "Bugün harika işler yapacaksın! 🌟",
      "Sanatçıların seni bekliyor! 🎸",
      "Yeni bir gün, yeni melodiler! 🎹",
      "Ritmi sen belirle! 🥁"
    ];

    const interval = setInterval(() => {
      setCurrentMessage(welcomeMessages[Math.floor(Math.random() * welcomeMessages.length)]);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative overflow-hidden bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl shadow-xl mb-8">
      <div className="p-6">
        {/* Welcome Header */}
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-white mb-2 flex items-center justify-center gap-2">
            Hoş geldin, {adminName}!
            <span className="animate-wave inline-block">👋</span>
          </h2>
          <p className="text-lg text-white/80">
            {currentMessage}
          </p>
        </div>

        {/* Music Studio */}
        <div className="bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-sm rounded-xl p-4 border border-white/10">
          {/* Instruments */}
          <div className="flex gap-2 mb-3">
            {(['piano', 'guitar', 'bass', 'synth', 'drums'] as Instrument[]).map((inst) => (
              <button
                key={inst}
                onClick={() => setInstrument(inst)}
                className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                  instrument === inst
                    ? 'bg-gradient-to-r from-orange-500 to-purple-600 text-white shadow-lg'
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                {inst === 'piano' ? '🎹' : inst === 'guitar' ? '🎸' : inst === 'bass' ? '🎵' : inst === 'synth' ? '🎛️' : '🥁'}
              </button>
            ))}
            <button
              onClick={() => setShowControls(!showControls)}
              className="px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-xs text-white/70 transition-all"
            >
              {showControls ? '▲' : '▼'}
            </button>
          </div>

          {/* Advanced Controls (Collapsible) */}
          {showControls && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              <div className="flex items-center gap-2 bg-white/5 rounded-lg px-2 py-1.5">
                <span className="text-[10px] text-white/60">Vol</span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={volume}
                  onChange={(e) => setVolume(parseFloat(e.target.value))}
                  className="flex-1 h-1 bg-white/20 rounded-lg appearance-none cursor-pointer slider"
                />
                <span className="text-[10px] text-white/60 min-w-[25px]">{Math.round(volume * 100)}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/5 rounded-lg px-2 py-1.5">
                <span className="text-[10px] text-white/60">Tempo</span>
                <input
                  type="range"
                  min="200"
                  max="800"
                  step="50"
                  value={tempo}
                  onChange={(e) => setTempo(parseInt(e.target.value))}
                  className="flex-1 h-1 bg-white/20 rounded-lg appearance-none cursor-pointer slider"
                />
              </div>
              <button
                onClick={() => setReverb(!reverb)}
                className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  reverb
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white'
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                Echo {reverb ? 'ON' : 'OFF'}
              </button>
              {instrument === 'piano' && (
                <select
                  value={waveform}
                  onChange={(e) => setWaveform(e.target.value as OscillatorType)}
                  className="px-2 py-1.5 bg-white/10 text-white/70 rounded-lg text-xs border border-white/20"
                >
                  <option value="sine">Sine</option>
                  <option value="square">Square</option>
                  <option value="sawtooth">Sawtooth</option>
                  <option value="triangle">Triangle</option>
                </select>
              )}
            </div>
          )}

          {/* Recording & Playback */}
          <div className="flex gap-2 mb-3 flex-wrap">
            {/* Track Studio Toggle */}
            <button
              onClick={() => setShowTrackStudio(!showTrackStudio)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                showTrackStudio
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
              title="Track Studio: Farklı enstrümanları kaydedin ve birleştirin"
            >
              🎚️ Studio {tracks.length > 0 && `(${tracks.length})`}
            </button>

            {!showTrackStudio && (
              <>
                <button
                  onClick={() => setIsRecording(!isRecording)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    isRecording
                      ? 'bg-red-500 text-white animate-pulse'
                      : 'bg-white/10 text-white/70 hover:bg-white/20'
                  }`}
                >
                  {isRecording ? '⏺' : '⏺'}
                </button>

                {recording.length > 0 && (
                  <>
                    <button
                      onClick={playRecording}
                      disabled={isLooping}
                      className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white/70 rounded-lg text-xs transition-all disabled:opacity-50"
                    >
                      ▶ ({recording.length})
                    </button>
                    <button
                      onClick={toggleLoop}
                      className={`px-3 py-1.5 rounded-lg text-xs transition-all ${
                        isLooping
                          ? 'bg-purple-500 text-white'
                          : 'bg-white/10 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      🔁
                    </button>
                    <button
                      onClick={clearRecording}
                      className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white/70 rounded-lg text-xs transition-all"
                    >
                      🗑
                    </button>
                  </>
                )}
              </>
            )}

            <button
              onClick={() => playPreset('happy_birthday')}
              className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white/70 rounded-lg text-xs transition-all ml-auto"
            >
              🎂
            </button>
            <button
              onClick={() => playPreset('jingle_bells')}
              className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white/70 rounded-lg text-xs transition-all"
            >
              🎅
            </button>
            <button
              onClick={() => playPreset('twinkle')}
              className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white/70 rounded-lg text-xs transition-all"
            >
              ⭐
            </button>
          </div>

          {/* Track Studio Panel */}
          {showTrackStudio && (
            <div className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-lg p-4 mb-3 border border-white/10">
              {/* Studio Header */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-medium text-white">Track Studio</div>
                  <div className="text-xs text-white/50">{tracks.length} track{tracks.length !== 1 ? 's' : ''}</div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {tracks.length > 0 && (
                    <>
                      {!isPlaying ? (
                        <button
                          onClick={playAllTracks}
                          className="px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded text-xs font-medium hover:shadow-lg transition-all"
                        >
                          ▶ Çal
                        </button>
                      ) : (
                        <button
                          onClick={stopPlayback}
                          className="px-3 py-1 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded text-xs font-medium hover:shadow-lg transition-all"
                        >
                          ⏹ Durdur
                        </button>
                      )}
                      <button
                        onClick={clearAllTracks}
                        className="px-3 py-1 bg-red-500/80 hover:bg-red-500 text-white rounded text-xs transition-all"
                      >
                        Temizle
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Timeline View - DAW Style */}
              {tracks.length > 0 ? (
                <div className="mb-3">
                  {/* Timeline Controls */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-white/60">Timeline Duration: {formatTime(getTimelineDuration())}</div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setTimelineZoom(Math.max(0.5, timelineZoom - 0.5))}
                        className="px-2 py-1 bg-white/10 hover:bg-white/20 text-white/70 rounded text-xs"
                        title="Zoom Out"
                      >
                        -
                      </button>
                      <span className="text-xs text-white/60 px-2 py-1">{Math.round(timelineZoom * 100)}%</span>
                      <button
                        onClick={() => setTimelineZoom(Math.min(3, timelineZoom + 0.5))}
                        className="px-2 py-1 bg-white/10 hover:bg-white/20 text-white/70 rounded text-xs"
                        title="Zoom In"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Timeline Grid */}
                  <div
                    className="relative bg-black/40 rounded-lg border border-white/10 overflow-x-auto overflow-y-hidden"
                    style={{ height: `${tracks.length * 60 + 40}px`, minHeight: '140px' }}
                    onMouseMove={handleTimelineMouseMove}
                    onMouseUp={handleTimelineMouseUp}
                    onMouseLeave={handleTimelineMouseUp}
                  >
                    {/* Time Ruler */}
                    <div className="sticky top-0 bg-gray-900/90 border-b border-white/10 h-8 flex items-center px-2 z-10">
                      {Array.from({ length: Math.ceil(getTimelineDuration() / 1000) }).map((_, i) => (
                        <div
                          key={i}
                          className="absolute text-[10px] text-white/40"
                          style={{ left: `${(i * 1000) / (10 / timelineZoom)}px` }}
                        >
                          {i}s
                        </div>
                      ))}
                    </div>

                    {/* Playback Progress Line */}
                    {isPlaying && (
                      <div
                        className="absolute top-0 bottom-0 w-0.5 bg-orange-500 shadow-lg z-20 pointer-events-none"
                        style={{
                          left: `${128 + (playbackProgress / (10 / timelineZoom))}px`,
                          boxShadow: '0 0 10px rgba(249, 115, 22, 0.8)'
                        }}
                      >
                        <div className="absolute -top-1 -left-1.5 w-3 h-3 bg-orange-500 rounded-full shadow-lg"></div>
                      </div>
                    )}

                    {/* Tracks */}
                    <div className="relative">
                      {tracks.map((track, index) => {
                        const trackWidth = (track.duration / (10 / timelineZoom));
                        const trackLeft = (track.startOffset / (10 / timelineZoom));

                        return (
                          <div
                            key={track.id}
                            className="relative border-b border-white/5"
                            style={{ height: '60px' }}
                          >
                            {/* Track Label */}
                            <div className="absolute left-0 top-0 bottom-0 w-32 bg-gray-900/60 border-r border-white/10 p-1.5 flex flex-col justify-between z-10">
                              <div className="flex items-start gap-1.5">
                                <div className="text-xs mt-0.5">{getInstrumentIcon(track.instrument)}</div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-[10px] font-medium text-white truncate leading-tight">{track.name}</div>
                                  <div className="text-[9px] text-white/40">{track.notes.length}</div>
                                </div>
                                <button
                                  onClick={() => deleteTrack(track.id)}
                                  className="px-1 py-0.5 bg-red-500/20 hover:bg-red-500/40 text-red-300 rounded text-[10px] transition-all leading-none"
                                >
                                  ✕
                                </button>
                              </div>
                              {/* Volume Control */}
                              <div className="flex items-center gap-1">
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.01"
                                  value={track.volume}
                                  onChange={(e) => updateTrackVolume(track.id, parseFloat(e.target.value))}
                                  className="flex-1 h-1 bg-white/20 rounded-lg appearance-none cursor-pointer slider"
                                  title={`Volume: ${Math.round(track.volume * 100)}%`}
                                />
                                <span className="text-[8px] text-white/50 min-w-[18px] text-right">{Math.round(track.volume * 100)}</span>
                              </div>
                            </div>

                            {/* Track Timeline Area */}
                            <div className="absolute left-32 right-0 top-0 bottom-0">
                              {/* Track Block */}
                              <div
                                className={`absolute top-2 bottom-2 rounded-lg cursor-move transition-all ${
                                  draggingTrackId === track.id ? 'opacity-80 scale-105 shadow-lg' : 'hover:opacity-90'
                                }`}
                                style={{
                                  left: `${trackLeft}px`,
                                  width: `${trackWidth}px`,
                                  backgroundColor: track.color,
                                  border: `2px solid ${track.color}`,
                                  boxShadow: `0 0 20px ${track.color}40`
                                }}
                                onMouseDown={(e) => handleTimelineMouseDown(track.id, e)}
                              >
                                {/* Track Content */}
                                <div className="h-full p-2 flex items-center justify-between">
                                  <div className="text-xs font-medium text-white/90 truncate flex-1">
                                    {formatTime(track.startOffset)} → {formatTime(track.startOffset + track.duration)}
                                  </div>
                                  <div className="text-[10px] text-white/60">{formatTime(track.duration)}</div>
                                </div>

                                {/* Waveform Visualization (Simplified) */}
                                <div className="absolute inset-0 flex items-center gap-0.5 px-2 pointer-events-none">
                                  {track.notes.slice(0, 30).map((note, i) => {
                                    const height = 20 + (Math.random() * 20);
                                    return (
                                      <div
                                        key={i}
                                        className="w-0.5 bg-white/30 rounded-full"
                                        style={{ height: `${height}%` }}
                                      ></div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Timeline Help Text */}
                  <div className="mt-2 text-[10px] text-white/40 text-center">
                    Trackları sürükleyerek zaman çizelgesinde hareket ettirebilirsiniz
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-white/40 text-xs">
                  <div>Henüz track yok. Kayda başla!</div>
                </div>
              )}

              {/* Preset Library */}
              <div className="mb-3">
                <button
                  onClick={() => setShowPresetMenu(!showPresetMenu)}
                  className="w-full px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all flex items-center justify-center gap-2"
                >
                  <span>Hazır Parça Ekle</span>
                  <span className="text-xs opacity-80">({Object.keys(presets).length})</span>
                  <span className="ml-auto">{showPresetMenu ? '▲' : '▼'}</span>
                </button>

                {showPresetMenu && (
                  <div className="mt-2 bg-black/40 border border-white/10 rounded-lg p-2 max-h-60 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(presets).map(([key, preset]) => (
                        <button
                          key={key}
                          onClick={() => {
                            addPresetToTimeline(key as keyof typeof presets);
                            setShowPresetMenu(false);
                          }}
                          className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs transition-all text-left flex items-center gap-2"
                        >
                          <span className="text-base">{getInstrumentIcon(preset.instrument)}</span>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{preset.name}</div>
                            <div className="text-[10px] text-white/50">{preset.notes.length} notes</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Recording Controls */}
              <div className="flex gap-2">
                {!isRecording ? (
                  <button
                    onClick={startTrackRecording}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <span>⏺</span>
                    <span className="hidden sm:inline">Yeni Track Kaydet</span>
                    <span className="sm:hidden">Kayıt</span>
                    <span className="text-xs opacity-80">({getInstrumentIcon(instrument)} {instrument})</span>
                  </button>
                ) : (
                  <button
                    onClick={stopTrackRecording}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium animate-pulse flex items-center justify-center gap-2"
                  >
                    <span>⏹</span>
                    <span className="hidden sm:inline">Kaydı Durdur</span>
                    <span className="sm:hidden">Durdur</span>
                    <span className="text-xs opacity-80">({currentTrackRecording.length})</span>
                  </button>
                )}
              </div>

              {/* Help Text */}
              <div className="mt-2 text-[10px] text-white/40 text-center">
                Tüm trackler aynı anda çalacak
              </div>
            </div>
          )}

          {/* Instrument Interface */}
          <div className="relative bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-4 rounded-lg backdrop-blur-sm border border-white/5">
            {/* Piano */}
            {instrument === 'piano' && (
              <div className="flex justify-center">
                <div className="inline-flex gap-0.5 relative">
                  {pianoKeys.map((key) => (
                    key.color === 'white' ? (
                      <button
                        key={key.note}
                        onClick={() => playSound(key.freq, key.note)}
                        className={`relative w-11 h-28 bg-gradient-to-b from-gray-50 to-white border border-gray-300/50 rounded-b-md shadow-md transition-all duration-100 hover:shadow-lg hover:from-white hover:to-gray-50 active:from-gray-100 active:to-gray-200 ${
                          playingNote === key.note ? 'transform scale-95 bg-gradient-to-b from-orange-300 to-orange-400 shadow-orange-500/50' : ''
                        }`}
                      >
                        <span className="absolute bottom-2 left-0 right-0 text-[10px] font-semibold text-gray-500 text-center">
                          {key.label}
                        </span>
                      </button>
                    ) : null
                  ))}

                  {/* Black Keys */}
                  {pianoKeys.map((key) => (
                    key.color === 'black' ? (
                      <button
                        key={key.note}
                        onClick={() => playSound(key.freq, key.note)}
                        style={{
                          position: 'absolute',
                          left: `${getBlackKeyPosition(key.note)}px`,
                          zIndex: 10
                        }}
                        className={`w-7 h-16 bg-gradient-to-b from-gray-900 to-black border border-black/50 rounded-b-md shadow-xl transition-all duration-100 hover:from-gray-800 hover:to-gray-900 active:from-orange-700 active:to-black ${
                          playingNote === key.note ? 'transform scale-95 bg-gradient-to-b from-orange-500 to-orange-700 shadow-orange-500/50' : ''
                        }`}
                      >
                        <span className="text-[9px] font-medium text-white/60 block mt-10 text-center">
                          {key.label}
                        </span>
                      </button>
                    ) : null
                  ))}
                </div>
              </div>
            )}

            {/* Guitar */}
            {instrument === 'guitar' && (
              <div className="flex justify-center items-center">
                <div className="relative">
                  {/* Guitar Body */}
                  <svg width="500" height="280" viewBox="0 0 500 280" className="mb-4">
                    {/* Guitar Body Shape */}
                    <ellipse cx="350" cy="140" rx="120" ry="130" fill="url(#guitarGradient)" stroke="#5d3a1a" strokeWidth="3"/>
                    <ellipse cx="350" cy="140" rx="90" ry="100" fill="url(#soundholeGradient)"/>
                    <circle cx="350" cy="140" r="40" fill="#1a1a1a"/>

                    {/* Neck */}
                    <rect x="30" y="110" width="240" height="60" fill="url(#neckGradient)" stroke="#5d3a1a" strokeWidth="2" rx="8"/>

                    {/* Frets */}
                    {[60, 100, 140, 180, 220, 260].map((x, i) => (
                      <line key={i} x1={x} y1="110" x2={x} y2="170" stroke="#d4af37" strokeWidth="1.5" opacity="0.6"/>
                    ))}

                    {/* Strings */}
                    {pianoKeys.filter(k => k.color === 'white').map((key, idx) => {
                      const stringY = 120 + (idx * 7);
                      const isPlaying = playingNote === key.note;
                      return (
                        <g key={key.note}>
                          <line
                            x1="30"
                            y1={stringY}
                            x2="470"
                            y2={stringY}
                            stroke={isPlaying ? "#ff6b35" : "#e8e8e8"}
                            strokeWidth={isPlaying ? "3" : "2"}
                            className="transition-all duration-100"
                            style={{
                              filter: isPlaying ? 'drop-shadow(0 0 8px #ff6b35)' : 'none'
                            }}
                          />
                          {/* Clickable area for each string */}
                          <rect
                            x="30"
                            y={stringY - 8}
                            width="440"
                            height="16"
                            fill="transparent"
                            className="cursor-pointer"
                            onClick={() => playSound(key.freq, key.note)}
                          />
                          {/* Note label on string */}
                          <text
                            x="50"
                            y={stringY + 4}
                            fill="white"
                            fontSize="12"
                            fontWeight="bold"
                            className="pointer-events-none"
                          >
                            {key.label}
                          </text>
                        </g>
                      );
                    })}

                    <defs>
                      <linearGradient id="guitarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style={{stopColor: '#d4a574', stopOpacity: 1}} />
                        <stop offset="100%" style={{stopColor: '#8b5a3c', stopOpacity: 1}} />
                      </linearGradient>
                      <radialGradient id="soundholeGradient">
                        <stop offset="0%" style={{stopColor: '#6b4423', stopOpacity: 1}} />
                        <stop offset="100%" style={{stopColor: '#3d2817', stopOpacity: 1}} />
                      </radialGradient>
                      <linearGradient id="neckGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" style={{stopColor: '#5d3a1a', stopOpacity: 1}} />
                        <stop offset="50%" style={{stopColor: '#8b5a3c', stopOpacity: 1}} />
                        <stop offset="100%" style={{stopColor: '#5d3a1a', stopOpacity: 1}} />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </div>
            )}

            {/* Bass */}
            {instrument === 'bass' && (
              <div className="flex justify-center items-center">
                <div className="relative">
                  {/* Bass Guitar Body */}
                  <svg width="500" height="220" viewBox="0 0 500 220" className="mb-4">
                    {/* Bass Body - More angular, P-Bass style */}
                    <path
                      d="M 320 60 Q 380 60 420 100 Q 460 140 420 180 Q 380 220 320 220 L 280 220 L 280 60 Z"
                      fill="url(#bassGradient)"
                      stroke="#2d1810"
                      strokeWidth="3"
                    />
                    <path
                      d="M 320 80 Q 360 80 390 110 Q 420 140 390 170 Q 360 200 320 200 L 290 200 L 290 80 Z"
                      fill="url(#bassBodyGradient)"
                      stroke="#1a0f08"
                      strokeWidth="2"
                    />

                    {/* Neck - longer and thinner */}
                    <rect x="30" y="90" width="260" height="40" fill="url(#bassNeckGradient)" stroke="#2d1810" strokeWidth="2" rx="6"/>

                    {/* Frets */}
                    {[60, 100, 140, 180, 220, 260].map((x, i) => (
                      <line key={i} x1={x} y1="90" x2={x} y2="130" stroke="#c0c0c0" strokeWidth="1.5" opacity="0.5"/>
                    ))}

                    {/* Bass Strings - Only 4 thicker strings */}
                    {pianoKeys.filter(k => k.color === 'white').slice(0, 4).map((key, idx) => {
                      const stringY = 98 + (idx * 10);
                      const isPlaying = playingNote === key.note;
                      return (
                        <g key={key.note}>
                          <line
                            x1="30"
                            y1={stringY}
                            x2="450"
                            y2={stringY}
                            stroke={isPlaying ? "#a855f7" : "#d0d0d0"}
                            strokeWidth={isPlaying ? "5" : "3.5"}
                            className="transition-all duration-100"
                            style={{
                              filter: isPlaying ? 'drop-shadow(0 0 10px #a855f7)' : 'none'
                            }}
                          />
                          {/* Clickable area */}
                          <rect
                            x="30"
                            y={stringY - 10}
                            width="420"
                            height="20"
                            fill="transparent"
                            className="cursor-pointer"
                            onClick={() => playSound(key.freq, key.note)}
                          />
                          {/* Note label */}
                          <text
                            x="50"
                            y={stringY + 5}
                            fill="white"
                            fontSize="14"
                            fontWeight="bold"
                            className="pointer-events-none"
                          >
                            {key.label}
                          </text>
                        </g>
                      );
                    })}

                    <defs>
                      <linearGradient id="bassGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style={{stopColor: '#4a4a4a', stopOpacity: 1}} />
                        <stop offset="100%" style={{stopColor: '#1a1a1a', stopOpacity: 1}} />
                      </linearGradient>
                      <linearGradient id="bassBodyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style={{stopColor: '#2d2d2d', stopOpacity: 1}} />
                        <stop offset="100%" style={{stopColor: '#0f0f0f', stopOpacity: 1}} />
                      </linearGradient>
                      <linearGradient id="bassNeckGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" style={{stopColor: '#3d2817', stopOpacity: 1}} />
                        <stop offset="50%" style={{stopColor: '#5d3a1a', stopOpacity: 1}} />
                        <stop offset="100%" style={{stopColor: '#3d2817', stopOpacity: 1}} />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </div>
            )}

            {/* Synth - Modular Synthesizer Panel */}
            {instrument === 'synth' && (
              <div className="flex justify-center">
                <div className="relative">
                  {/* Synth Panel Background */}
                  <div className="bg-gradient-to-br from-gray-800 via-gray-900 to-black rounded-lg p-6 border-4 border-gray-700 shadow-2xl" style={{width: '520px'}}>
                    {/* Top LED Display */}
                    <div className="bg-cyan-900/30 border border-cyan-500/30 rounded p-2 mb-4 flex items-center gap-2">
                      <div className="flex gap-1">
                        {[1,2,3].map(i => (
                          <div key={i} className={`w-2 h-2 rounded-full ${playingNote ? 'bg-cyan-400 animate-pulse' : 'bg-cyan-800'}`}></div>
                        ))}
                      </div>
                      <div className="flex-1 h-8 bg-black/50 rounded border border-cyan-500/20 flex items-center px-3">
                        <span className="text-cyan-400 text-xs font-mono">
                          {playingNote ? `♪ ${playingNote}` : 'READY'}
                        </span>
                      </div>
                    </div>

                    {/* Pad Grid - Modular style */}
                    <div className="grid grid-cols-4 gap-3">
                      {pianoKeys.map((key, idx) => (
                        <button
                          key={key.note}
                          onClick={() => playSound(key.freq, key.note)}
                          className={`relative w-28 h-28 rounded-lg transition-all duration-100 font-bold border-4 ${
                            playingNote === key.note
                              ? 'bg-gradient-to-br from-cyan-300 to-blue-500 shadow-lg shadow-cyan-500/50 scale-95 border-cyan-300'
                              : key.color === 'white'
                              ? 'bg-gradient-to-br from-cyan-600 to-blue-700 hover:from-cyan-500 hover:to-blue-600 border-cyan-700'
                              : 'bg-gradient-to-br from-pink-600 to-purple-700 hover:from-pink-500 hover:to-purple-600 border-pink-700'
                          }`}
                        >
                          {/* LED indicator in corner */}
                          <div className={`absolute top-2 right-2 w-3 h-3 rounded-full ${
                            playingNote === key.note ? 'bg-white shadow-lg shadow-white/50' : 'bg-gray-600'
                          }`}></div>

                          {/* Note label */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-center">
                              <div className="text-white text-2xl font-bold">{key.label}</div>
                              <div className="text-white/40 text-xs mt-1">{key.note}</div>
                            </div>
                          </div>

                          {/* Metallic shine effect */}
                          <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-lg pointer-events-none"></div>
                        </button>
                      ))}
                    </div>

                    {/* Bottom control strip */}
                    <div className="mt-4 flex gap-2">
                      {[1,2,3,4,5,6].map(i => (
                        <div key={i} className="flex-1 h-1.5 bg-gradient-to-r from-orange-600 to-purple-600 rounded-full shadow-inner"></div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Drums - Realistic Drum Kit */}
            {instrument === 'drums' && (
              <div className="flex justify-center items-center">
                <div className="relative">
                  <svg width="600" height="400" viewBox="0 0 600 400" className="mb-4">
                    {/* Kick Drum - Center bottom */}
                    <g className="cursor-pointer" onClick={() => playSound(pianoKeys[0].freq, pianoKeys[0].note)}>
                      <ellipse cx="300" cy="340" rx="80" ry="25" fill="url(#kickDrumBase)" />
                      <ellipse cx="300" cy="280" rx="80" ry="80" fill="url(#kickDrumGradient)" stroke="#1a1a1a" strokeWidth="3"/>
                      <ellipse cx="300" cy="280" rx="60" ry="60" fill="url(#kickDrumHead)" />
                      <circle cx="300" cy="280" r="25" fill="#0f0f0f" opacity="0.3"/>
                      <text x="300" y="285" fill="white" fontSize="14" fontWeight="bold" textAnchor="middle" className="pointer-events-none">Kick</text>
                      {playingNote === pianoKeys[0].note && (
                        <circle cx="300" cy="280" r="85" fill="none" stroke="#ff6b35" strokeWidth="4" opacity="0.6">
                          <animate attributeName="r" from="60" to="95" dur="0.3s" />
                          <animate attributeName="opacity" from="0.8" to="0" dur="0.3s" />
                        </circle>
                      )}
                    </g>

                    {/* Snare Drum - Left */}
                    <g className="cursor-pointer" onClick={() => playSound(pianoKeys[1].freq, pianoKeys[1].note)}>
                      <ellipse cx="180" cy="270" rx="60" ry="18" fill="url(#snareBase)" />
                      <ellipse cx="180" cy="230" rx="60" ry="60" fill="url(#snareGradient)" stroke="#2d2d2d" strokeWidth="3"/>
                      <ellipse cx="180" cy="230" rx="48" ry="48" fill="url(#drumHead)" />
                      <text x="180" y="235" fill="white" fontSize="12" fontWeight="bold" textAnchor="middle" className="pointer-events-none">Snare</text>
                      {playingNote === pianoKeys[1].note && (
                        <circle cx="180" cy="230" r="65" fill="none" stroke="#ff6b35" strokeWidth="3" opacity="0.6">
                          <animate attributeName="r" from="48" to="75" dur="0.3s" />
                          <animate attributeName="opacity" from="0.8" to="0" dur="0.3s" />
                        </circle>
                      )}
                    </g>

                    {/* Hi-Hat - Top Left */}
                    <g className="cursor-pointer" onClick={() => playSound(pianoKeys[2].freq, pianoKeys[2].note)}>
                      {/* Stand */}
                      <line x1="100" y1="180" x2="100" y2="280" stroke="#4a4a4a" strokeWidth="3"/>
                      {/* Top Cymbal */}
                      <ellipse cx="100" cy="160" rx="55" ry="12" fill="url(#cymbalGradient)" stroke="#ffd700" strokeWidth="2"/>
                      <ellipse cx="100" cy="160" rx="50" ry="8" fill="#ffeaa7" opacity="0.6"/>
                      {/* Bottom Cymbal (slightly visible) */}
                      <ellipse cx="100" cy="168" rx="55" ry="8" fill="#d4af37" opacity="0.4"/>
                      <text x="100" y="145" fill="white" fontSize="11" fontWeight="bold" textAnchor="middle" className="pointer-events-none">Hi-Hat</text>
                      {playingNote === pianoKeys[2].note && (
                        <ellipse cx="100" cy="160" rx="60" ry="15" fill="none" stroke="#ffd700" strokeWidth="3" opacity="0.6">
                          <animate attributeName="rx" from="50" to="65" dur="0.2s" />
                          <animate attributeName="ry" from="8" to="18" dur="0.2s" />
                          <animate attributeName="opacity" from="0.8" to="0" dur="0.2s" />
                        </ellipse>
                      )}
                    </g>

                    {/* Tom 1 - Top Center */}
                    <g className="cursor-pointer" onClick={() => playSound(pianoKeys[3].freq, pianoKeys[3].note)}>
                      <ellipse cx="250" cy="165" rx="50" ry="15" fill="url(#tomBase)" />
                      <ellipse cx="250" cy="130" rx="50" ry="50" fill="url(#tomGradient1)" stroke="#8b0000" strokeWidth="2"/>
                      <ellipse cx="250" cy="130" rx="40" ry="40" fill="url(#drumHead)" />
                      <text x="250" y="135" fill="white" fontSize="11" fontWeight="bold" textAnchor="middle" className="pointer-events-none">Tom1</text>
                      {playingNote === pianoKeys[3].note && (
                        <circle cx="250" cy="130" r="55" fill="none" stroke="#ff6b35" strokeWidth="3" opacity="0.6">
                          <animate attributeName="r" from="40" to="60" dur="0.3s" />
                          <animate attributeName="opacity" from="0.8" to="0" dur="0.3s" />
                        </circle>
                      )}
                    </g>

                    {/* Tom 2 - Top Right */}
                    <g className="cursor-pointer" onClick={() => playSound(pianoKeys[4].freq, pianoKeys[4].note)}>
                      <ellipse cx="350" cy="170" rx="52" ry="16" fill="url(#tomBase)" />
                      <ellipse cx="350" cy="135" rx="52" ry="52" fill="url(#tomGradient2)" stroke="#8b0000" strokeWidth="2"/>
                      <ellipse cx="350" cy="135" rx="42" ry="42" fill="url(#drumHead)" />
                      <text x="350" y="140" fill="white" fontSize="11" fontWeight="bold" textAnchor="middle" className="pointer-events-none">Tom2</text>
                      {playingNote === pianoKeys[4].note && (
                        <circle cx="350" cy="135" r="57" fill="none" stroke="#ff6b35" strokeWidth="3" opacity="0.6">
                          <animate attributeName="r" from="42" to="62" dur="0.3s" />
                          <animate attributeName="opacity" from="0.8" to="0" dur="0.3s" />
                        </circle>
                      )}
                    </g>

                    {/* Floor Tom - Right */}
                    <g className="cursor-pointer" onClick={() => playSound(pianoKeys[5].freq, pianoKeys[5].note)}>
                      <ellipse cx="450" cy="285" rx="65" ry="20" fill="url(#tomBase)" />
                      <ellipse cx="450" cy="240" rx="65" ry="65" fill="url(#tomGradient3)" stroke="#8b0000" strokeWidth="3"/>
                      <ellipse cx="450" cy="240" rx="52" ry="52" fill="url(#drumHead)" />
                      <text x="450" y="245" fill="white" fontSize="12" fontWeight="bold" textAnchor="middle" className="pointer-events-none">Floor</text>
                      {playingNote === pianoKeys[5].note && (
                        <circle cx="450" cy="240" r="70" fill="none" stroke="#ff6b35" strokeWidth="3" opacity="0.6">
                          <animate attributeName="r" from="52" to="78" dur="0.3s" />
                          <animate attributeName="opacity" from="0.8" to="0" dur="0.3s" />
                        </circle>
                      )}
                    </g>

                    {/* Crash Cymbal - Right */}
                    <g className="cursor-pointer" onClick={() => playSound(pianoKeys[6].freq, pianoKeys[6].note)}>
                      <line x1="520" y1="110" x2="500" y2="250" stroke="#4a4a4a" strokeWidth="3"/>
                      <ellipse cx="530" cy="90" rx="60" ry="14" fill="url(#cymbalGradient)" stroke="#ffd700" strokeWidth="2"/>
                      <ellipse cx="530" cy="90" rx="55" ry="10" fill="#ffeaa7" opacity="0.5"/>
                      <text x="530" y="75" fill="white" fontSize="11" fontWeight="bold" textAnchor="middle" className="pointer-events-none">Crash</text>
                      {playingNote === pianoKeys[6].note && (
                        <ellipse cx="530" cy="90" rx="65" ry="16" fill="none" stroke="#ffd700" strokeWidth="3" opacity="0.6">
                          <animate attributeName="rx" from="55" to="70" dur="0.3s" />
                          <animate attributeName="ry" from="10" to="18" dur="0.3s" />
                          <animate attributeName="opacity" from="0.8" to="0" dur="0.3s" />
                        </ellipse>
                      )}
                    </g>

                    <defs>
                      <radialGradient id="kickDrumGradient">
                        <stop offset="0%" style={{stopColor: '#b22222', stopOpacity: 1}} />
                        <stop offset="100%" style={{stopColor: '#8b0000', stopOpacity: 1}} />
                      </radialGradient>
                      <radialGradient id="kickDrumHead">
                        <stop offset="0%" style={{stopColor: '#f0f0f0', stopOpacity: 1}} />
                        <stop offset="100%" style={{stopColor: '#c0c0c0', stopOpacity: 1}} />
                      </radialGradient>
                      <linearGradient id="kickDrumBase">
                        <stop offset="0%" style={{stopColor: '#4a0000', stopOpacity: 1}} />
                        <stop offset="100%" style={{stopColor: '#2d0000', stopOpacity: 1}} />
                      </linearGradient>
                      <radialGradient id="snareGradient">
                        <stop offset="0%" style={{stopColor: '#e0e0e0', stopOpacity: 1}} />
                        <stop offset="100%" style={{stopColor: '#a0a0a0', stopOpacity: 1}} />
                      </radialGradient>
                      <linearGradient id="snareBase">
                        <stop offset="0%" style={{stopColor: '#5a5a5a', stopOpacity: 1}} />
                        <stop offset="100%" style={{stopColor: '#3a3a3a', stopOpacity: 1}} />
                      </linearGradient>
                      <radialGradient id="drumHead">
                        <stop offset="0%" style={{stopColor: '#ffffff', stopOpacity: 0.9}} />
                        <stop offset="50%" style={{stopColor: '#f5f5f5', stopOpacity: 0.8}} />
                        <stop offset="100%" style={{stopColor: '#d0d0d0', stopOpacity: 0.9}} />
                      </radialGradient>
                      <radialGradient id="tomGradient1">
                        <stop offset="0%" style={{stopColor: '#dc143c', stopOpacity: 1}} />
                        <stop offset="100%" style={{stopColor: '#a01020', stopOpacity: 1}} />
                      </radialGradient>
                      <radialGradient id="tomGradient2">
                        <stop offset="0%" style={{stopColor: '#cd5c5c', stopOpacity: 1}} />
                        <stop offset="100%" style={{stopColor: '#a03030', stopOpacity: 1}} />
                      </radialGradient>
                      <radialGradient id="tomGradient3">
                        <stop offset="0%" style={{stopColor: '#b22222', stopOpacity: 1}} />
                        <stop offset="100%" style={{stopColor: '#8b0000', stopOpacity: 1}} />
                      </radialGradient>
                      <linearGradient id="tomBase">
                        <stop offset="0%" style={{stopColor: '#4a0000', stopOpacity: 1}} />
                        <stop offset="100%" style={{stopColor: '#2d0000', stopOpacity: 1}} />
                      </linearGradient>
                      <radialGradient id="cymbalGradient">
                        <stop offset="0%" style={{stopColor: '#ffd700', stopOpacity: 1}} />
                        <stop offset="50%" style={{stopColor: '#ffed4e', stopOpacity: 1}} />
                        <stop offset="100%" style={{stopColor: '#d4af37', stopOpacity: 1}} />
                      </radialGradient>
                    </defs>
                  </svg>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      <style>{`
        @keyframes wave {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(20deg); }
          75% { transform: rotate(-20deg); }
        }
        .animate-wave {
          animation: wave 1s ease-in-out infinite;
        }

        /* Custom slider styling */
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: linear-gradient(135deg, #f97316 0%, #a855f7 100%);
          cursor: pointer;
          box-shadow: 0 0 5px rgba(249, 115, 22, 0.5);
        }

        .slider::-moz-range-thumb {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: linear-gradient(135deg, #f97316 0%, #a855f7 100%);
          cursor: pointer;
          border: none;
          box-shadow: 0 0 5px rgba(249, 115, 22, 0.5);
        }

        .slider::-webkit-slider-thumb:hover {
          box-shadow: 0 0 10px rgba(249, 115, 22, 0.8);
        }

        .slider::-moz-range-thumb:hover {
          box-shadow: 0 0 10px rgba(249, 115, 22, 0.8);
        }
      `}</style>
    </div>
  );
};

// Siyah tuşların pozisyonlarını hesapla
// Beyaz tuşlar w-11 (44px) genişliğinde ve gap-0.5 (2px) aralıklı
// Siyah tuşlar beyaz tuşların arasına yerleştirilmeli (gerçek piyano gibi)
const getBlackKeyPosition = (note: string): number => {
  const whiteKeyWidth = 44; // w-11 = 44px in Tailwind
  const gap = 2; // gap-0.5 = 2px
  const keyUnit = whiteKeyWidth + gap; // Her beyaz tuş + boşluk = 46px
  const blackKeyWidth = 28; // w-7 = 28px

  // Siyah tuşlar beyaz tuşların arasına ortalanmalı
  // C# -> C ve D arasında (1. beyaz tuşun sonunda)
  // D# -> D ve E arasında (2. beyaz tuşun sonunda)
  // F# -> F ve G arasında (4. beyaz tuşun sonunda - E-F arası yok)
  // G# -> G ve G# arasında (5. beyaz tuşun sonunda)
  // A# -> A ve B arasında (6. beyaz tuşun sonunda)

  const positions: { [key: string]: number } = {
    'C#': (keyUnit * 1) - (blackKeyWidth / 2) - gap,      // C'den sonra
    'D#': (keyUnit * 2) - (blackKeyWidth / 2) - gap,      // D'den sonra
    'F#': (keyUnit * 4) - (blackKeyWidth / 2) - gap,      // F'den sonra (E-F arası yok)
    'G#': (keyUnit * 5) - (blackKeyWidth / 2) - gap,      // G'den sonra
    'A#': (keyUnit * 6) - (blackKeyWidth / 2) - gap,      // A'dan sonra
  };
  return positions[note] || 0;
};

export default WelcomePiano;
